import React, { useEffect, useMemo, useRef, useState } from "react";
import PracticeLayout from "../../../components/PracticeLayout";
import PracticeSection from "../../../components/PracticeSection";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Loader2, Trash2 } from "lucide-react";
import useDocumentTitle from "../../../hooks/useDocumentTitle";

export default function LevelDynamic() {
  useDocumentTitle("Level 5 · Dynamic UI — Practice");
  const [tags, setTags] = useState(["alpha", "beta"]);
  const [addBusy, setAddBusy] = useState(false);
  const [ajaxState, setAjaxState] = useState({ loading: false, data: null });
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [items, setItems] = useState(() => Array.from({ length: 20 }, (_, i) => `Item ${i + 1}`));
  const [role, setRole] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [college, setCollege] = useState("");
  const [rows, setRows] = useState(() => [
    { id: 1, name: "Selenium",   users: 4820, updated: "2026-02-01" },
    { id: 2, name: "Playwright", users: 3200, updated: "2026-02-05" },
    { id: 3, name: "Cypress",    users: 2900, updated: "2026-01-28" },
    { id: 4, name: "k6",         users: 1750, updated: "2026-02-04" },
  ]);
  const [sort, setSort] = useState({ key: "id", dir: "asc" });
  const [filter, setFilter] = useState("");

  // Dynamic add — waits 800ms to simulate a real create call.
  const addTag = async () => {
    setAddBusy(true);
    await new Promise((r) => setTimeout(r, 800));
    setTags((t) => [...t, `dyn-${Date.now().toString().slice(-4)}`]);
    setAddBusy(false);
  };

  const removeTag = (t) => setTags((all) => all.filter((x) => x !== t));

  // Fake AJAX call
  const fetchData = async () => {
    setAjaxState({ loading: true, data: null });
    await new Promise((r) => setTimeout(r, 1200));
    setAjaxState({
      loading: false,
      data: { timestamp: new Date().toISOString(), quote: "Automate the boring; leave the interesting to humans." },
    });
  };

  // Loading button
  const runLoading = async () => {
    setLoadingBtn(true);
    await new Promise((r) => setTimeout(r, 1500));
    setLoadingBtn(false);
  };

  // Infinite scroll
  const sentinelRef = useRef();
  useEffect(() => {
    if (!sentinelRef.current) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setItems((cur) => [...cur, ...Array.from({ length: 10 }, (_, i) => `Item ${cur.length + i + 1}`)]);
      }
    }, { rootMargin: "50px" });
    io.observe(sentinelRef.current);
    return () => io.disconnect();
  }, []);

  const sorted = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows
      .filter((r) => !q || r.name.toLowerCase().includes(q))
      .sort((a, b) => {
        const [av, bv] = [a[sort.key], b[sort.key]];
        const cmp = av < bv ? -1 : av > bv ? 1 : 0;
        return sort.dir === "asc" ? cmp : -cmp;
      });
  }, [rows, sort, filter]);

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  return (
    <PracticeLayout
      eyebrow="Level 5 · Dynamic UI"
      title="Elements that appear over time"
      subtitle="Where 90 % of flaky tests are born. Practise smart waits, retries and idempotent locators."
      seo={{
        title: "Level 5 · Dynamic UI — AJAX, Infinite Scroll, Dynamic Tables",
        description: "Practise AJAX waits, loading states, infinite scroll, conditional fields and dynamic tables with sort, filter and delete.",
        keywords: "ajax automation practice, infinite scroll automation, dynamic table automation, loading state testing",
        image: "/og/og-dynamic.svg",
      }}
    >
      <PracticeSection id="dynamic-elements" title="Dynamic elements" hint="Wait for the new chip to appear before asserting.">
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="dynamic-chip-list">
          {tags.map((t) => (
            <span key={t} data-testid={`dynamic-chip-${t}`} className="tag-mono text-xs bg-blue-50 border border-blue-200 text-blue-800 px-2 py-0.5 rounded inline-flex items-center gap-1">
              {t}
              <button onClick={() => removeTag(t)} data-testid={`dynamic-chip-remove-${t}`} className="text-blue-600 hover:text-blue-900">×</button>
            </span>
          ))}
        </div>
        <Button onClick={addTag} disabled={addBusy} data-testid="dynamic-add-btn" className="bg-[#002FA7] hover:bg-[#00227A] text-white">
          {addBusy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Adding…</> : "Add chip (800 ms delay)"}
        </Button>
      </PracticeSection>

      <PracticeSection id="ajax" title="AJAX" hint="Trigger the fetch and wait for `[data-testid=ajax-response]`.">
        <Button onClick={fetchData} disabled={ajaxState.loading} data-testid="ajax-fetch-btn" className="bg-[#002FA7] hover:bg-[#00227A] text-white">
          {ajaxState.loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Fetching…</> : "Fetch data"}
        </Button>
        {ajaxState.loading && (
          <div data-testid="ajax-loading" className="mt-3 text-sm tag-mono text-zinc-500">loading…</div>
        )}
        {ajaxState.data && (
          <div data-testid="ajax-response" className="mt-3 border border-emerald-200 bg-emerald-50 rounded p-3 text-sm">
            <div className="tag-mono text-[10px] text-emerald-700 mb-1">200 OK · {ajaxState.data.timestamp}</div>
            <div className="text-emerald-900">{ajaxState.data.quote}</div>
          </div>
        )}
      </PracticeSection>

      <PracticeSection id="loading" title="Loading state" hint="Button is `disabled` while loading — assert both.">
        <Button onClick={runLoading} disabled={loadingBtn} data-testid="loading-btn" className="bg-[#002FA7] hover:bg-[#00227A] text-white">
          {loadingBtn ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing…</> : "Run task (1.5 s)"}
        </Button>
      </PracticeSection>

      <PracticeSection id="infinite-scroll" title="Infinite scroll" hint="Scroll to the bottom to load 10 more.">
        <div className="border border-zinc-200 rounded bg-white h-56 overflow-y-auto" data-testid="infinite-container">
          <ul className="divide-y divide-zinc-100 tag-mono text-sm">
            {items.map((it, i) => (
              <li key={i} data-testid={`infinite-row-${i}`} className="px-3 py-1.5">{it}</li>
            ))}
          </ul>
          <div ref={sentinelRef} data-testid="infinite-sentinel" className="p-3 text-center text-xs text-zinc-400 tag-mono">
            loading more…
          </div>
        </div>
        <div className="tag-mono text-[11px] text-zinc-500 mt-1" data-testid="infinite-count">
          rows: {items.length}
        </div>
      </PracticeSection>

      <PracticeSection id="conditional" title="Conditional fields" hint="Extra fields only appear based on the role choice.">
        <div className="grid sm:grid-cols-2 gap-3 max-w-lg">
          <div>
            <Label>Role</Label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid="cond-role"
              className="mt-1.5 h-10 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm"
            >
              <option value="">— choose —</option>
              <option value="student">Student</option>
              <option value="professional">Professional</option>
            </select>
          </div>
          {role === "student" && (
            <div data-testid="cond-college-wrap">
              <Label>College</Label>
              <Input value={college} onChange={(e) => setCollege(e.target.value)} data-testid="cond-college" className="mt-1.5" />
            </div>
          )}
          {role === "professional" && (
            <div data-testid="cond-company-wrap">
              <Label>Company</Label>
              <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} data-testid="cond-company" className="mt-1.5" />
            </div>
          )}
        </div>
      </PracticeSection>

      <PracticeSection id="dynamic-table" title="Dynamic table" hint="Sort by any column, filter, and delete rows.">
        <div className="mb-2 max-w-xs">
          <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by name…" data-testid="table-filter" />
        </div>
        <div className="border border-zinc-200 rounded overflow-hidden bg-white">
          <table className="w-full text-sm" data-testid="dynamic-table">
            <thead className="bg-zinc-50 text-[10px] tag-mono uppercase tracking-widest text-zinc-500">
              <tr>
                {[["id","ID"], ["name","Name"], ["users","Users"], ["updated","Updated"]].map(([k, label]) => (
                  <th
                    key={k}
                    onClick={() => toggleSort(k)}
                    data-testid={`table-th-${k}`}
                    className="text-left px-3 py-2 cursor-pointer hover:text-zinc-900"
                  >
                    {label} {sort.key === k ? (sort.dir === "asc" ? "▲" : "▼") : ""}
                  </th>
                ))}
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.id} data-testid={`table-row-${r.id}`} className="border-t border-zinc-100">
                  <td className="px-3 py-2 tag-mono">{r.id}</td>
                  <td className="px-3 py-2">{r.name}</td>
                  <td className="px-3 py-2">{r.users.toLocaleString()}</td>
                  <td className="px-3 py-2 tag-mono">{r.updated}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => setRows((rs) => rs.filter((x) => x.id !== r.id))}
                      data-testid={`table-delete-${r.id}`}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr><td colSpan="5" data-testid="table-empty" className="px-3 py-4 text-center text-zinc-500 text-xs">No matches</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </PracticeSection>
    </PracticeLayout>
  );
}
